import { subscribeListener, unsubscribeListener } from 'redux/ably/sagas';
import config from '../../redux/chart-iq/chart/config';
import { getChartData } from '../../redux/chart/api';

// const LIMIT = 1000;

export interface IOldOhlcResponse {
  close: string,
  high: string,
  low: string,
  open: string,
  date: string
  percentile10: string
  percentile90: string
  tftf10_open: string
  tftf90_open: string
  rvol: number
  rvolmin: number
  time: string
  signal10_action: number,
  signal90_action: number
  vix: number
}

interface IOhlcResponse {
  olhc: {
    c: string,
    h: string,
    interval: "1second" | "1minute" | "1minute" | "1hour" | "1day",
    l: string,
    o: string,
    v: string
    vwap: string
    time: string
  },
  aggregate: {
    time: string
    avg_rv: null
    percentile10: number
    percentile90: number
    signal_early_stop: string
    signal_ob: string
    signal_os: string
    sym: string
    tftf10: number
    tftf90: number
  }
  rvol: {
    time: string
    type: "tick"
    value: number
  },
  rvolmin: {
    time: string
    type: "tick"
    value: number
  },
  vix: {
    price: number
    time: string
  },
  lawr: {
    price: string,
    time: string
  },
  tj1: {
    value: number,
    time: string
  },
  r3m2h: {
    value: number,
    time: string
  },
  r3m2n: {
    value: number,
    time: string
  },
  r1m2: {
    value: number,
    time: string
  },
  b2x5: {
    value: number,
    time: string
  },
  r3m2r5: {
    long: number,
    short: number,
    time: string
  },
  experiment1: {
    value: number,
    time: string
  },
  experiment2: {
    value: number,
    time: string
  }
  experiment3: {
    value: number,
    time: string
  },
  experiment4: {
    value: number,
    time: string
  },
  experiment5: {
    value: number,
    time: string
  },
  experiment6: {
    value: number,
    time: string
  },
  experiment7: {
    value: number,
    time: string
  }
  experiment8: {
    value: number,
    time: string
  },
  experiment9: {
    value: number,
    time: string
  },
  experiment10: {
    value: number,
    time: string
  },
  velocity: {
    value: number,
    time: string
  }
}

interface ISubscribeParameters {
  symbol: string
  interval: string
  period: string
}

export interface IChartIQData {
  Close: number,
  Date: string,
  High: number,
  Low: number,
  Open: number,
  Percentile10: number,
  Percentile90: number,
  LAWR: number,
  TJ1: number,
  R3M2H: number,
  R3M2N: number,
  R1M2: number,
  B2X5: number,
  R3M2R5L: number,
  R3M2R5S: number,
  Experiment1: number,
  Experiment2: number,
  Experiment3: number,
  Experiment4: number,
  Experiment5: number,
  Experiment6: number,
  Experiment7: number,
  Experiment8: number,
  Experiment9: number,
  Experiment10: number,
  Velocity: number,
  Rvol: number,
  Rvolmin: number,
  Signal10_action: 1 | 0,
  Signal90_action: -1 | 0,
  SignalStop_action: -2 | 0,
  Tftf10_open: number,
  Tftf90_open: number,
  Vix: number,
  Volume: number,
}

export const Quotefeed = (id: number, token: string, ciq: any, setIsLoadingPeriodicity: (loading: boolean) => void, dataRangeCb: (minDate: Date) => void, setTotalTicks: (tickCount: number) => void) => ({
  fetchInitialData: async (symbol: string, startDate: Date, endDate: Date, params: any, cb: (params: any) => void) => {
    const { period, interval } = params;
    const symbolConfig = config.symbols.find(sym => sym.id === symbol);

    if (symbolConfig) {
      setIsLoadingPeriodicity(true);
      const query = `olhc?symbol=${symbolConfig.baseSymbol}&model=${symbol}&start=${startDate.toISOString()}&stop=${endDate.toISOString()}&interval=${period}${interval}`

      Promise.all([
        getChartData(
          query
        ),
      ]).then(results => {
        setIsLoadingPeriodicity(false);
        if (results[0].errors.length > 0) { cb({ quotes: []}); return;}
        if (results[0].meta && results[0].meta[0]) {
          if (results[0].meta[0].oldest) {
            dataRangeCb(new Date(results[0].meta[0].oldest))
          } else if (results[0].meta[0].type === 'tick') {
            const tickData = results[0].data as any[];
            const ticks = tickData.reverse().map(({ olhc, rvol, rvolmin, vix, lawr, tj1, r3m2h, r3m2n, r1m2, b2x5, r3m2r5, experiment1, experiment2, experiment3, experiment4, experiment5, experiment6, experiment7, experiment8, experiment9, experiment10, velocity }) => ({
              Close: olhc.close,
              Date: olhc.time,
              High: olhc.close,
              Low: olhc.close,
              Open: olhc.close,
              Volume: olhc.volume,
              Percentile10: olhc.percentile10,
              Percentile90: olhc.percentile90,
              Signal10_action: olhc.signal10_action,
              Signal90_action: olhc.signal90_action,
              SignalStop_action: olhc.signalstop_action,
              Tftf10_open: olhc.tftf10_open,
              Tftf90_open: olhc.tftf90_open,
              LAWR: lawr ? parseFloat(lawr.price) : null,
              TJ1: tj1.value,
              R3M2H: r3m2h.value,
              R3M2N: r3m2n.value,
              R1M2: r1m2.value,
              B2X5: b2x5.value,
              R3M2R5L: r3m2r5.long,
              R3M2R5S: r3m2r5.short,
              Experiment1: experiment1 ? experiment1.value : 0,
              Experiment2: experiment2 ? experiment2.value : 0,
              Experiment3: experiment3 ? experiment3.value : 0,
              Experiment4: experiment4 ? experiment4.value : 0,
              Experiment5: experiment5 ? experiment5.value : 0,
              Experiment6: experiment6 ? experiment6.value : 0,
              Experiment7: experiment7 ? experiment7.value : 0,
              Experiment8: experiment8 ? experiment8.value : 0,
              Experiment9: experiment9 ? experiment9.value : 0,
              Experiment10: experiment10 ? experiment10.value : 0,
              Velocity: velocity ? velocity.value : 0,
              Rvol: rvol.value,
              Rvolmin: rvolmin.value,
              Vix: vix.price,
            }));
            cb({ quotes: ticks });

            if (ciq.masterData) {
              setTotalTicks(ciq.masterData.length)
            }
            return;
          }
        }
        const olhcData = results[0].data as IOhlcResponse[];
        const quotes = olhcData.reverse().map(({ olhc, aggregate, rvol, rvolmin, vix, lawr, tj1, r3m2h, r3m2n, r1m2, b2x5, r3m2r5, experiment1, experiment2, experiment3, experiment4, experiment5, experiment6, experiment7, experiment8, experiment9, experiment10, velocity }) => ({
          Close: parseFloat(olhc.c),
          Date: olhc.time,
          High: parseFloat(olhc.h),
          Low: parseFloat(olhc.l),
          Open: parseFloat(olhc.o),
          Volume: parseInt(olhc.v, 10),
          Percentile10: aggregate.percentile10,
          Percentile90: aggregate.percentile90,
          Signal10_action: parseInt(aggregate.signal_os || "0", 10) !== 0 ? 1 : 0,
          Signal90_action: parseInt(aggregate.signal_ob || "0", 10) !== 0 ? -1 : 0,
          SignalStop_action: parseInt(aggregate.signal_early_stop || "0", 10) !== 0 ? -2 : 0,
          Tftf10_open: aggregate.tftf10,
          Tftf90_open: aggregate.tftf90,
          LAWR: lawr ? parseFloat(lawr.price) : null,
          TJ1: tj1.value,
          R3M2H: r3m2h ? r3m2h.value : 0,
          R3M2N: r3m2n ? r3m2n.value : 0,
          R1M2: r1m2 ? r1m2.value : 0,
          B2X5: b2x5 ? b2x5.value : 0,
          R3M2R5L: r3m2r5 ? r3m2r5.long : 0,
          R3M2R5S: r3m2r5 ? r3m2r5.short : 0,
          Experiment1: experiment1 ? experiment1.value : 0,
          Experiment2: experiment2 ? experiment2.value : 0,
          Experiment3: experiment3 ? experiment3.value : 0,
          Experiment4: experiment4 ? experiment4.value : 0,
          Experiment5: experiment5 ? experiment5.value : 0,
          Experiment6: experiment6 ? experiment6.value : 0,
          Experiment7: experiment7 ? experiment7.value : 0,
          Experiment8: experiment8 ? experiment8.value : 0,
          Experiment9: experiment9 ? experiment9.value : 0,
          Experiment10: experiment10 ? experiment10.value : 0,
      Velocity: velocity ? velocity.value : 0,
          Rvol: rvol.value,
          Rvolmin: rvolmin.value,
          Vix: vix.price,
        }));
        cb({ quotes });
        if (ciq.masterData) {
          setTotalTicks(ciq.masterData.length)
        }

      });
    }
  },
  fetchPaginationData: (symbol: string, suggestedStartDate: Date, endDate: Date, params: any, cb: (params: any) => void) => {
    const { period, interval } = params;
    const symbolConfig = config.symbols.find(sym => sym.id === symbol);
    if (symbolConfig) {
      setIsLoadingPeriodicity(true);
      const query = `olhc?symbol=${symbolConfig.baseSymbol}&model=${symbol}&start=${suggestedStartDate.toISOString()}&stop=${endDate.toISOString()}&interval=${period}${interval}`

      Promise.all([
        getChartData(
          query
        ),
      ]).then(results => {
        setIsLoadingPeriodicity(false);
        let moreAvailable = false;
        if (results[0].errors.length > 0) { cb({ quotes: [], moreAvailable}); return;}
        if (results[0].meta && results[0].meta[0] && results[0].meta[0].type === 'tick') {
          const tickData = results[0].data as any[];
          const ticks = tickData.reverse().map(({ olhc, rvol, rvolmin, vix, lawr, tj1, r3m2h, r3m2n, r1m2, b2x5, r3m2r5, experiment1, experiment2, experiment3, experiment4, experiment5, experiment6, experiment7, experiment8, experiment9, experiment10, velocity }) => ({
            Close: olhc.close,
            Date: olhc.time,
            High: olhc.close,
            Low: olhc.close,
            Open: olhc.close,
            Volume: olhc.volume,
            Percentile10: olhc.percentile10,
            Percentile90: olhc.percentile90,
            Signal10_action: olhc.signal10_action,
            Signal90_action: olhc.signal90_action,
            SignalStop_action: olhc.signalstop_action,
            Tftf10_open: olhc.tftf10_open,
            Tftf90_open: olhc.tftf90_open,
            LAWR: lawr ? parseFloat(lawr.price) : null,
            TJ1: tj1.value,
            R3M2H: r3m2h.value,
            R3M2N: r3m2n.value,
            R1M2: r1m2.value,
            B2X5: b2x5.value,
            R3M2R5L: r3m2r5.long,
            R3M2R5S: r3m2r5.short,
            Experiment1: experiment1 ? experiment1.value : 0,
            Experiment2: experiment2 ? experiment2.value : 0,
            Experiment3: experiment3 ? experiment3.value : 0,
            Experiment4: experiment4 ? experiment4.value : 0,
            Experiment5: experiment5 ? experiment5.value : 0,
            Experiment6: experiment6 ? experiment6.value : 0,
            Experiment7: experiment7 ? experiment7.value : 0,
            Experiment8: experiment8 ? experiment8.value : 0,
            Experiment9: experiment9 ? experiment9.value : 0,
            Experiment10: experiment10 ? experiment10.value : 0,
            Velocity: velocity.value,
            Rvol: rvol.value,
            Rvolmin: rvolmin.value,
            Vix: vix.price,
          }));
          cb({ quotes: ticks });
          if (ciq.masterData) {
            setTotalTicks(ciq.masterData.length)
          }
          return;
        }
        const olhcData = results[0].data as IOhlcResponse[];
        const quotes = olhcData.reverse().map(({ olhc, aggregate, rvol, rvolmin, vix, lawr, tj1, r3m2h, r3m2n, r1m2, b2x5, r3m2r5, experiment1, experiment2, experiment3, experiment4, experiment5, experiment6, experiment7, experiment8, experiment9, experiment10, velocity }) => ({
          Close: parseFloat(olhc.c),
          Date: olhc.time,
          High: parseFloat(olhc.h),
          Low: parseFloat(olhc.l),
          Open: parseFloat(olhc.o),
          Volume: parseInt(olhc.v, 10),
          Percentile10: aggregate.percentile10,
          Percentile90: aggregate.percentile90,
          Signal10_action: parseInt(aggregate.signal_os || "0", 10) !== 0 ? 1 : 0,
          Signal90_action: parseInt(aggregate.signal_ob || "0", 10) !== 0 ? -1 : 0,
          SignalStop_action: parseInt(aggregate.signal_early_stop || "0", 10) !== 0 ? -2 : 0,
          Tftf10_open: aggregate.tftf10,
          Tftf90_open: aggregate.tftf90,
          LAWR: lawr ? parseFloat(lawr.price) : null,
          TJ1: tj1.value,
          R3M2H: r3m2h ? r3m2h.value : 0,
          R3M2N: r3m2n ? r3m2n.value : 0,
          R1M2: r1m2 ? r1m2.value : 0,
          B2X5: b2x5 ? b2x5.value : 0,
          R3M2R5L: r3m2r5 ? r3m2r5.long : 0,
          R3M2R5S: r3m2r5 ? r3m2r5.short : 0,
          Experiment1: experiment1 ? experiment1.value : 0,
          Experiment2: experiment2 ? experiment2.value : 0,
          Experiment3: experiment3 ? experiment3.value : 0,
          Experiment4: experiment4 ? experiment4.value : 0,
          Experiment5: experiment5 ? experiment5.value : 0,
          Experiment6: experiment6 ? experiment6.value : 0,
          Experiment7: experiment7 ? experiment7.value : 0,
          Experiment8: experiment8 ? experiment8.value : 0,
          Experiment9: experiment9 ? experiment9.value : 0,
          Experiment10: experiment10 ? experiment10.value : 0,
        Velocity: velocity ? velocity.value : 0,
          Rvol: rvol.value,
          Rvolmin: rvolmin.value,
          Vix: vix.price,
        }));
        moreAvailable = quotes.length > 0


        if (results[0].meta[0]) {
          moreAvailable = results[0].meta[0].moreData;
        }
        cb({ quotes, moreAvailable });
        if (ciq.masterData) {
          setTotalTicks(ciq.masterData.length)
        }
      });
    }
  },
  subscribe: (payload: ISubscribeParameters) => {
    const symbolConfig = config.symbols.find(sym => sym.id === payload.symbol);
    if (symbolConfig) {
      subscribeListener(
        `model:${symbolConfig.model}:${payload.period}${payload.interval}`,
        id,
        (data: any) => {
          if (ciq.highlandLookbackMode) { return }
          // data.data should be an array
          if (!Array.isArray(data.data)) { return }
          ciq.updateChartData(data.data, null, { allowReplaceOHL: true });
          if (ciq.masterData) {
            setTotalTicks(ciq.masterData.length)
          }
        },
      );
    }
  },
  unsubscribe: (payload: ISubscribeParameters) => {
    const symbolConfig = config.symbols.find(sym => sym.id === payload.symbol);
    if (symbolConfig) {
        unsubscribeListener(
          `model:${symbolConfig.model}:${payload.period}${payload.interval}`,
          id,
        );
    }
  },
});
